import { useState } from "react";
import { Eye, KeyRound, ListChecks, RefreshCw, RotateCcw, Terminal, Webhook } from "lucide-react";
import DeliveryLog from "@/components/DeliveryLog";

const BASE = process.env.REACT_APP_BACKEND_URL;

export default function DevPlayground() {
  const [apiKey, setApiKey] = useState(() => localStorage.getItem("docketscout_demo_key") || "");
  const [webhookUrl, setWebhookUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState("");
  const [error, setError] = useState("");
  const [searchResult, setSearchResult] = useState("");
  const [watchResult, setWatchResult] = useState("");
  const [deliveries, setDeliveries] = useState(null);

  const api = async (path, opts = {}) => {
    const res = await fetch(`${BASE}${path}`, {
      ...opts,
      headers: {
        "Content-Type": "application/json",
        ...(apiKey ? { "X-API-Key": apiKey } : {}),
        ...opts.headers,
      },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Request failed");
    return data;
  };

  const fetchDeliveries = async () => {
    if (!apiKey) return;
    try {
      setDeliveries(await api("/api/deliveries"));
    } catch (e) {
      setError(e.message);
    }
  };

  const generateKey = async () => {
    setBusy(true); setError(""); setResult("");
    try {
      const data = await api("/api/keys", { method: "POST", body: JSON.stringify({ name: "playground" }) });
      setApiKey(data.key);
      localStorage.setItem("docketscout_demo_key", data.key);
      setResult(`Key created: ${data.key}`);
      fetchDeliveries();
    } catch (e) { setError(e.message); }
    setBusy(false);
  };

  const registerWebhook = async () => {
    if (!apiKey) { setError("Generate an API key first."); return; }
    if (!webhookUrl.trim()) { setError("Enter your webhook URL."); return; }
    setBusy(true); setError(""); setResult("");
    try {
      const hook = await api("/api/webhooks", { method: "POST", body: JSON.stringify({ url: webhookUrl.trim() }) });
      const test = await api(`/api/webhooks/${hook.id}/test`, { method: "POST" });
      setResult(
        test.delivered
          ? `Webhook registered (${hook.id}). Test ping delivered — HTTP ${test.status_code}. Signing secret: ${hook.secret}`
          : `Webhook registered (${hook.id}). Test ping could not be delivered (HTTP ${test.status_code}) — it will retry with backoff; watch the delivery log below.`
      );
      fetchDeliveries();
    } catch (e) { setError(e.message); }
    setBusy(false);
  };

  const runTestSearch = async () => {
    if (!apiKey) { setError("Generate an API key first."); return; }
    setBusy(true); setError(""); setSearchResult("");
    try {
      const data = await api("/api/v1/searches", {
        method: "POST",
        body: JSON.stringify({
          mode: "party", first: "Robert", last: "Callahan",
          county: "Kings", caseType: "Post Judgment CM", clientRef: "playground-001",
        }),
      });
      setSearchResult(JSON.stringify(data, null, 2));
      fetchDeliveries();
    } catch (e) { setError(e.message); }
    setBusy(false);
  };

  const watchAndMatch = async () => {
    if (!apiKey) { setError("Generate an API key first."); return; }
    setBusy(true); setError(""); setWatchResult("");
    try {
      const watch = await api("/api/v1/watches", {
        method: "POST",
        body: JSON.stringify({ mode: "party", first: "Robert", last: "Callahan", county: "Kings", clientRef: "playground-watch" }),
      });
      const match = await api(`/api/v1/watches/${watch.id}/matches`, {
        method: "POST",
        body: JSON.stringify({
          case_number: "012345/2026",
          court: "Kings County Supreme Court",
          case_type: "Post Judgment CM",
          filed_date: "2026-10-06",
        }),
      });
      setWatchResult(
        match.deliveries_queued > 0
          ? `Watching "${watch.party}" (watch ${watch.id}). Test match pushed — ${match.deliveries_queued} signed case.matched delivery queued.`
          : `Watching "${watch.party}" (watch ${watch.id}). No webhook registered yet — register one in step 2 so match events have somewhere to go.`
      );
      fetchDeliveries();
    } catch (e) { setError(e.message); }
    setBusy(false);
  };

  const replay = async (id) => {
    setError("");
    try {
      await api(`/api/deliveries/${id}/replay`, { method: "POST" });
      setTimeout(fetchDeliveries, 1500);
    } catch (e) { setError(e.message); }
  };

  return (
    <div className="rounded-lg border border-white/10 bg-white/5 p-6 md:p-8" data-testid="dev-playground">
      <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-crimson">
        <ZapIcon /> Live playground
      </p>
      <h3 className="mt-3 font-display text-2xl">Try it without leaving the page</h3>

      <div className="mt-6 space-y-4">
        <div>
          <p className="mb-2 text-xs text-ivory/60">
            <KeyRound size={12} className="mr-1.5 inline" />
            {apiKey ? `Using stored key ${apiKey.slice(0, 12)}…` : "Step 1 — generate a demo API key."}
          </p>
          <button
            onClick={generateKey}
            disabled={busy}
            data-testid="dev-key-generate-button"
            className="inline-flex items-center gap-2 rounded-md bg-crimson px-4 py-2.5 text-xs font-bold tracking-wide text-ivory transition-colors hover:bg-ivory hover:text-ink disabled:opacity-50"
          >
            <KeyRound size={13} /> {apiKey ? "Regenerate key" : "Generate demo key"}
          </button>
        </div>

        <div>
          <p className="mb-2 text-xs text-ivory/60">Step 2 — point a webhook at your machine and we send a signed test ping.</p>
          <input
            value={webhookUrl}
            onChange={(e) => setWebhookUrl(e.target.value)}
            placeholder="https://yourapp.com/hooks/docketscout"
            data-testid="dev-webhook-url-input"
            className="w-full rounded-md border border-white/15 bg-black/30 px-3.5 py-2.5 font-mono text-xs text-ivory placeholder:text-ivory/30 outline-none transition-colors focus:border-crimson"
          />
          <button
            onClick={registerWebhook}
            disabled={busy}
            data-testid="dev-webhook-register-button"
            className="mt-2 inline-flex items-center gap-2 rounded-md border border-white/20 px-4 py-2.5 text-xs font-bold tracking-wide text-ivory transition-colors hover:border-crimson hover:bg-crimson disabled:opacity-50"
          >
            <Webhook size={13} /> Register &amp; send test ping
          </button>
        </div>

        <div>
          <p className="mb-2 text-xs text-ivory/60">Step 3 — create a search through the API, exactly as your systems would.</p>
          <button
            onClick={runTestSearch}
            disabled={busy}
            data-testid="dev-test-search-button"
            className="inline-flex items-center gap-2 rounded-md border border-white/20 px-4 py-2.5 text-xs font-bold tracking-wide text-ivory transition-colors hover:border-crimson hover:bg-crimson disabled:opacity-50"
          >
            <Terminal size={13} /> Run test search
          </button>
        </div>

        <div>
          <p className="mb-2 text-xs text-ivory/60">Step 4 — watch a party and push a test match; your webhooks receive a signed case.matched event with automatic retries.</p>
          <button
            onClick={watchAndMatch}
            disabled={busy}
            data-testid="dev-watch-match-button"
            className="inline-flex items-center gap-2 rounded-md border border-white/20 px-4 py-2.5 text-xs font-bold tracking-wide text-ivory transition-colors hover:border-crimson hover:bg-crimson disabled:opacity-50"
          >
            <Eye size={13} /> Create watch &amp; push test match
          </button>
        </div>

        {error && (
          <p role="alert" data-testid="dev-playground-error" className="text-xs font-semibold text-crimson">{error}</p>
        )}
        {result && (
          <p data-testid="dev-playground-result" className="break-all rounded-md border border-white/10 bg-black/40 p-3 font-mono text-[11px] leading-relaxed text-ivory/80">{result}</p>
        )}
        {watchResult && (
          <p data-testid="dev-playground-watch-result" className="break-all rounded-md border border-white/10 bg-black/40 p-3 font-mono text-[11px] leading-relaxed text-ivory/80">{watchResult}</p>
        )}
        {searchResult && (
          <pre data-testid="dev-playground-search-result" className="max-h-72 overflow-auto rounded-md border border-white/10 bg-black/40 p-3 font-mono text-[11px] leading-relaxed text-ivory/80">{searchResult}</pre>
        )}

        <div className="border-t border-white/10 pt-5">
          <div className="flex items-center justify-between">
            <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-crimson">
              <ListChecks size={13} /> Delivery log
            </p>
            <button
              onClick={fetchDeliveries}
              data-testid="dev-deliveries-refresh"
              className="inline-flex items-center gap-1.5 rounded px-2 py-1 text-[11px] font-semibold text-ivory/60 transition-colors hover:bg-white/10 hover:text-ivory"
            >
              <RefreshCw size={12} /> Refresh
            </button>
          </div>
          {apiKey ? (
            <DeliveryLog deliveries={deliveries} onReplay={replay} />
          ) : (
            <p className="mt-3 text-xs text-ivory/50">Generate a key and run a step above — every webhook attempt lands here.</p>
          )}
        </div>
      </div>
    </div>
  );
}

function ZapIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  );
}
